import {Component, OnDestroy, OnInit,
	ViewChild}              from '@angular/core';
import {NgbDate}            from "@ng-bootstrap/ng-bootstrap/datepicker/ngb-date";
import {Router}             from "@angular/router";

import {Message}            from "../../services/message/message";
import {MessageEntry}       from "../../services/message/message-entry";
import {MessageService}     from "../../services/message/message.service";
import {NewsService}		from "../../services/news/news-service";
import {Query}              from "../../services/query/query";
import {User}               from "../../services/user/user";
import {UserService}        from "../../services/user/user.service";

import {forkJoin}           from "rxjs/observable/forkJoin";
import {isNullOrUndefined}  from "util";
import {Subscription}       from "rxjs/Subscription";
import { News }       		from 'app/services/news/news';


@Component({
  selector: 'user-reporting',
  templateUrl: './user-reporting.component.html',
  styleUrls: ['./user-reporting.component.css']
})


/**
 * Manages the User Reporting screen in the administration
 * menu.  It allows a Super User to get a CSV dump of
 * information about user activities during a selected
 * time period.
 */
export class UserReportingComponent implements OnInit, OnDestroy {

	disableButton: boolean = true;
	disableCopyButton: boolean = true;
	disableArticleButton: boolean = true;
	fromDate: string = null;
	message: Message;
	subscription: Subscription;
	toDate:string = null;
	users: User[];
	userSubscription: Subscription;
	hideArticleTable: boolean = true;
	articleList: News[] = [];
	lastFetchedLength: number = 0;
	nextOffsetMultiplier: number = 0;
	nextArticleUser: string;
	disableNextArticleButton:boolean = true;	


	/**
	 * Create the object and set up what it needs to talk to.
	 *
	 * @param {MessageService} messageService      Handles communication with the message bar.
	 * @param {Router} router                      The Router singleton used for navigation.
	 * @param {UserService} userService            Retrieves and stores user data.
	 */
	constructor(

		private messageService: MessageService,
		private router: Router,
		private userService: UserService,
		private newsService: NewsService

	) {}


	/**
	 * Unsubscribe to the subscriptions to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.subscription) {
			this.subscription.unsubscribe();
		}

		if (this.userSubscription) {
			this.userSubscription.unsubscribe();
		}

	}

	/**
	 * Set up communications with the message service.
	 */
	ngOnInit() {

		// Set up communications with the MessageService.
		this.subscription = this.messageService.subject.subscribe( message => { this.message = message; } );

		// Retrieve the User list.
		this.userSubscription = this.userService.getUsers().subscribe(
			(response:User[]) => {

				this.users = response;
				let msg:string = '';

				if (1 === response.length) {

					msg = '1 user found';

				} else {

					msg = response.length.toString() + ' users found'

				}

				this.messageService.sendMessageEntry(new MessageEntry({

					message: msg,
					level: 1

				}));

			},
			(error) => {

				this.messageService.sendMessageEntry(new MessageEntry({

					message: 'Unable to retrieve users. ' + Query.getQueryErrorMessage(error),
					level: 4

				}));

			}
		);

	}


	/**
	 * Lets me talk to the textarea used for the Clipboard save-to-text routine.
	 */
	@ViewChild('cboard')
	clipboardTextArea: any;


	/**
	 * Lets me talk to the 'From' datepicker.
	 */
	@ViewChild('from')
	from: any;


	/**
	 * Lets me talk to the 'To' datepicker.
	 */
	@ViewChild('to')
	to: any;


	/**
	 * Lets me talk to the user SELECT object.
	 */
	@ViewChild('userSelect')
	userSelect: any;


	/**
	 * Return false if the provided array is not an array or if it is zero length.
	 *
	 * @param {any} array    An array.
	 * @return {boolean} False if it's not an array or the array is zero length; true otherwise.
	 */
	checkArray(array:any): boolean {

		return (Array.isArray(array)) && (0 !== array.length);

	}


	/**
	 * Copy the output CSV text to the clipboard.
	 * NOTE: This has to be called from a button on a
	 * page to make it work.  This is because the
	 * document.execCommand function will not work
	 * unless there is user interaction with it.
	 *
	 * The Copy to Clipboard button is only
	 * available when there is valid data stored
	 * to clipboardTextArea.
	 *
	 */
	copyToClipboard(): void {

		let selection:Selection = document.getSelection();
		selection.removeAllRanges();
		this.clipboardTextArea.nativeElement.select();
		this.clipboardTextArea.nativeElement.focus();
		document.execCommand('copy');

		// Notify the user
		this.messageService.sendMessageEntry(new MessageEntry({

			message: 'Information copied to the clipboard',
			level: 1

		}));

	}


	/**
	 * Create the string array with actions table field names.
	 *
	 * @return {string[]}  The actions table field names.
	 */
	createActionsFields(): string[] {

		let actions_fields:string[] = [];
		actions_fields.push('fname');
		actions_fields.push('lname');
		actions_fields.push('action');
		actions_fields.push('news_player_id');
		actions_fields.push('logged');
		actions_fields.push('session_id');
		return actions_fields;

	}


	/**
	 * Create the string array with actions table headings.
	 *
	 * @return {string[]}  The actions table headings.
	 */
	createActionsHeader(): string[] {

		let actions_header:string[] = [];
		actions_header.push('First Name');
		actions_header.push('Last Name');
		actions_header.push('Action');
		actions_header.push('Action ID');
		actions_header.push('Time');
		actions_header.push('Session ID');
		return actions_header;

	}


	/**
	 * Create the string array with sessions table field names.
	 *
	 * @return {string[]}  The field names.
	 */
	createSessionsFields(): string[] {

		let sessions_fields:string[] = [];
		sessions_fields.push('fname');
		sessions_fields.push('lname');
		sessions_fields.push('start');
		sessions_fields.push('end');
		sessions_fields.push('minutes');
		sessions_fields.push('session_id');
		return sessions_fields;

	}


	/**
	 * Create the string array with sessions table headings.
	 *
	 * @return {string[]} The sessions table headings.
	 */
	createSessionsHeader(): string[] {

		let sessions_header:string[] = [];
		sessions_header.push('First Name');
		sessions_header.push('Last Name');
		sessions_header.push('Start Time');
		sessions_header.push('End Time');
		sessions_header.push('Minutes');
		sessions_header.push('Session ID');
		return sessions_header;

	}


	/**
	 * If the supplied data is an array, read it
	 * into a formatted CSV string and return it.
	 *
	 * @param {string[]}  headers  An array of header names that matches the supplied data.
	 * @param {string[]}  fields   An array of field names that matches the supplied data.
	 * @param {any}       array    An array of data that matches the header format.
	 * @return {string}  The formatted CSV data or null if there was an error.
	 */
	formatCSV(headers: string[], fields: string[], array:any): string {

		// Output constants.
		const FIELD_SEPARATOR:string    = ',';
		const LINE_ENDER:string         = '\r\n';
		const STRING_DELIMITER:string   = '"';

		// Make sure the input is properly formatted.
		if (! Array.isArray(array)) {
			console.log('The data was not an array.');
			return null;
		}

		if (! Array.isArray(headers)) {
			console.log('user-reporting.component.ts#formatCSV: The headers were not provided as an array.');
			return null;
		}

		if (! Array.isArray(fields)) {
			console.log('user-reporting.component.ts#formatCSV: The fields were not provided as an array.');
			return null;
		}

		if (fields.length !== headers.length) {
			console.log('user-reporting.component.ts#formatCSV: The fields and headers arrays were not the same lengths.');
			return null;
		}

		let pattern:RegExp  = /^\d+$/;
		let csv: string     = '';
		let first: boolean  = true;
		let text: string    = '';


		// Create the header string.
		for (let header of headers) {

			// Start the entry with a separator, except for the first entry.
			if (first) {
				first = false;
			} else {
				csv = csv.concat(FIELD_SEPARATOR)
			}

			// Append the text after escaping it, if necessary.
			csv = csv.concat(STRING_DELIMITER, header.replace(STRING_DELIMITER, '\'' + STRING_DELIMITER), STRING_DELIMITER);

		}

		// Add the line ender.
		csv = csv.concat(LINE_ENDER);

		// Append the data.
		for (let entry of array) {

			first = true;

			for (let field of fields) {

				// Start the entry with a separator, except for the first entry.
				if (first) {
					first = false;
				} else {
					csv = csv.concat(FIELD_SEPARATOR)
				}

				// Append the number by itself or the text after escaping it, if necessary.
				text = entry[field].toString();
				if (text.match(pattern)) {
					csv = csv.concat(text);
				} else {
					csv = csv.concat(STRING_DELIMITER, text.replace(STRING_DELIMITER, '\'' + STRING_DELIMITER), STRING_DELIMITER);
				}

			}

			// Add the line ender.
			csv = csv.concat(LINE_ENDER);
		}

		return csv.concat(LINE_ENDER, LINE_ENDER);

	}


	/**
	 * Get the requested data.
	 */
	retrieveData(): void {

		// Retrieve the user IDs.
		let users:string = '';
		for (let option of this.userSelect.nativeElement.selectedOptions) {
			if (0 !== users.length) {

				users = users.concat(',', option.value)

			} else {

				users = option.value;

			}

		}

		// Get the date information.
		this.setFromDate(this.from.selectedDate);
		this.setToDate(this.to.selectedDate);

		// Use ForkJoin to perform 2 separate queries to get the actions and sessions information for designated Users.
		this.clipboardTextArea.nativeElement.textContent = '';  // Clear the field where the data will go.
		this.disableCopyButton = true;
		let msg:string          = '';
		let level:number        = 1;
		let operations:any[]    = [];
		operations.push(this.userService.report(users, this.fromDate, this.toDate, 'user_actions'));
		operations.push(this.userService.report(users, this.fromDate, this.toDate, 'user_sessions'));

		// On success, forkJoin returns the data as an array, data[0] is actions, data[1] is sessions.
		forkJoin(operations).subscribe(
			(data) => {

				// Create the header and field arrays for the actions.
				let actions_header:string[] = this.createActionsHeader();
				let actions_fields:string[] = this.createActionsFields();
				let actions_csv: string = this.formatCSV(actions_header, actions_fields, data[0]);
				let sessions_header:string[] = this.createSessionsHeader();
				let sessions_fields:string[] = this.createSessionsFields();
				let sessions_csv: string = this.formatCSV(sessions_header, sessions_fields, data[1]);

				if (isNullOrUndefined(actions_csv)) {

					msg = 'Error while processing actions table information.';
					level = 4;

				} else if (isNullOrUndefined(sessions_csv)) {

					msg = 'Error while processing sessions table information.';
					level = 4;

				} else {

					if (this.checkArray(data[0]) || this.checkArray(data[1])) {

						msg = 'Data is ready; press "Copy to Clipboard" to save';
						level = 1;
						this.clipboardTextArea.nativeElement.textContent = actions_csv + sessions_csv;
						this.disableCopyButton = false;

					} else {

						msg = 'No data were available for the users and time range selected';
						level = 2;

					}

				}

			},
			(error) => {

				msg = 'Unable to retrieve user actions. ' + Query.getQueryErrorMessage(error);
				level = 4;

			},
			() => {

				this.messageService.sendMessageEntry(new MessageEntry({

					message: msg,
					level: level

				}));

			}

		);

	}


	/**
	 * Allows the date picker child component
	 * to set the from date in this component.
	 *
	 * @param {NgbDate} choice  The year/month/day from the date picker, or null.
	 */
	setFromDate(choice: NgbDate): void {

		if (isNullOrUndefined(choice)) {

			this.fromDate = null;

		} else {

			let month: string = (9 < choice.month) ? String(choice.month) : '0' + String(choice.month);
			let day: string = (9 < choice.day) ? String(choice.day) : '0' + String(choice.day);
			this.fromDate = choice.year + '-' + month + '-' + day;

		}

	}


	/**
	 * Allows the date picker child component
	 * to set the to date in this component.
	 *
	 * @param {NgbDate} choice  The year/month/day from the date picker, or null.
	 */
	setToDate(choice: NgbDate): void {

		if (isNullOrUndefined(choice)) {

			this.toDate = null;

		} else {

			let month: string = (9 < choice.month) ? String(choice.month) : '0' + String(choice.month);
			let day: string = (9 < choice.day) ? String(choice.day) : '0' + String(choice.day);
			this.toDate = choice.year + '-' + month + '-' + day;

		}

	}


	/**
	 * Turn the 'Retrieve User Data' button on or off
	 * depending on whether there are items selected.
	 *
	 */
	testButton(): void {

		this.disableButton = (0 === this.userSelect.nativeElement.selectedOptions.length);
		this.disableArticleButton = (1 < this.userSelect.nativeElement.selectedOptions.length)

	}


	/**
	 * Switch back to the story list.
	 *
	 */
	viewStoryList(): void {

		this.router.navigate(['/articles/list']);

	}

	getSelectedUserIds(): string {
		// Retrieve the user IDs.
		let users:string = '';
		for (let option of this.userSelect.nativeElement.selectedOptions) {
			if (0 !== users.length) {

				users = users.concat(',', option.value)

			} else {

				users = option.value;

			}

		}
		return users;
	}

	fetchUserArticles(user:string, offsetMultiplier: number = 0): void{

		let searchParams = {
			user_id: parseInt(user),
			startdate: this.fromDate,
			enddate: this.toDate
		}
			
		this.newsService.searchNewsItems(searchParams, 100, 100 * offsetMultiplier).subscribe(
			(result: News[]) => {
				this.articleList.push(...result);
				this.lastFetchedLength = result.length;
			},
			(error)=>{

			},
			()=>{
				if (this.lastFetchedLength > 0 && this.lastFetchedLength == 100) {
					this.nextOffsetMultiplier++;
					this.nextArticleUser = user;
					this.disableNextArticleButton = false;
				} else {
					this.disableNextArticleButton = true;
				}
			}
		);
	}

	displayArticleTable(): void {
		this.hideArticleTable = true;
		this.articleList = [];
		this.lastFetchedLength = 0;

		let user_ids: string = this.getSelectedUserIds();
		let users: string[] = user_ids.split(',')

		// Get the date information.
		this.setFromDate(this.from.selectedDate);
		this.setToDate(this.to.selectedDate);

		users.forEach((user)=>{
			this.fetchUserArticles(user, 0);
		})

		this.hideArticleTable = false;
	}

}



// WEBPACK FOOTER //
// ./src/app/admin/users/user-reporting.component.ts